import React, { useMemo } from 'react';
import {
  Bell,
  BookOpen,
  Camera,
  ChevronRight,
  Heart,
  Mic,
  Sparkles,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

const PROMPTS = [
  {
    id: 'photo',
    icon: Camera,
    label: 'Add their first photo',
    action: 'documents',
    hint: 'profile image or document',
  },
  {
    id: 'welcome',
    icon: BookOpen,
    label: 'Add birth or welcome story',
    action: 'stories',
  },
  {
    id: 'messages',
    icon: Heart,
    label: 'Add messages from family',
    action: 'stories',
  },
  {
    id: 'voice',
    icon: Mic,
    label: 'Record a voice message',
    action: 'stories',
    focus: 'voice',
  },
  {
    id: 'milestone',
    icon: Sparkles,
    label: 'Add first milestone',
    action: 'markers',
  },
  {
    id: 'reminder',
    icon: Bell,
    label: 'Create birthday reminder',
    action: 'reminders',
  },
];

/**
 * Gentle First Chapter prompts — optional, never required.
 */
export function FirstChapterProfileCard({
  person,
  canEdit = false,
  hasProfilePhoto = false,
  storyCount = 0,
  voiceMemoriesCount = 0,
  documentCount = 0,
  reminderCount = 0,
  onNavigate,
  className,
}) {
  const openPrompts = useMemo(() => {
    const done = new Set();
    if (hasProfilePhoto || documentCount > 0) done.add('photo');
    if (storyCount > 0) {
      done.add('welcome');
      done.add('messages');
    }
    if (voiceMemoriesCount > 0) done.add('voice');
    if (reminderCount > 0) done.add('reminder');
    return PROMPTS.filter((p) => !done.has(p.id)).slice(0, 4);
  }, [hasProfilePhoto, storyCount, voiceMemoriesCount, documentCount, reminderCount]);

  const handlePrompt = (prompt) => {
    if (typeof onNavigate !== 'function') return;
    if (prompt.action === 'reminders') {
      onNavigate('reminders');
      return;
    }
    if (prompt.action === 'markers') {
      onNavigate('markers');
      return;
    }
    if (prompt.action === 'documents') {
      onNavigate('documents');
      return;
    }
    onNavigate('stories', prompt.focus ? { focus: prompt.focus } : undefined);
  };

  const name = person?.given_names?.trim() || 'them';

  return (
    <section
      className={cn(
        'rounded-[1.5rem] border border-[#c9a961]/25 bg-gradient-to-br from-[hsl(42_28%_99%)] via-[hsl(40_24%_98%)] to-[hsl(38_22%_96%)] px-6 py-6 shadow-[inset_0_1px_0_hsl(42_40%_100%_/_0.55)] sm:px-8 sm:py-7',
        className
      )}
      data-testid="first-chapter-profile-card"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">First Chapter</p>
      <h3 className="mt-2 font-serif text-xl font-light text-[hsl(24_12%_18%)]">
        The beginning of {name}&apos;s story
      </h3>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted-foreground">
        Welcome a new life into the family archive. Preserve photos, messages, voice notes and first milestones —
        gently, at your own pace.
      </p>

      {canEdit && openPrompts.length > 0 ? (
        <ul className="mt-5 space-y-2">
          {openPrompts.map((prompt) => {
            const Icon = prompt.icon;
            return (
              <li key={prompt.id}>
                <button
                  type="button"
                  onClick={() => handlePrompt(prompt)}
                  className="flex w-full items-center gap-3 rounded-xl border border-[hsl(38_14%_88%)]/80 bg-white/70 px-4 py-3 text-left transition hover:border-[#c9a961]/35 hover:bg-white"
                  data-testid={`first-chapter-prompt-${prompt.id}`}
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] text-[#8b7355]">
                    <Icon className="h-4 w-4" strokeWidth={1.35} aria-hidden />
                  </span>
                  <span className="min-w-0 flex-1 text-sm text-foreground/85">{prompt.label}</span>
                  <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/40" aria-hidden />
                </button>
              </li>
            );
          })}
        </ul>
      ) : null}

      <p className="mt-5 text-[11px] leading-relaxed text-muted-foreground/75">
        Optional later: birth time, name meaning, welcome story, guardians, family blessings — all supported as your archive grows.
      </p>

      <div className="mt-4 flex flex-wrap items-center gap-3 border-t border-[hsl(38_14%_88%)]/60 pt-4">
        <p className="text-xs text-muted-foreground">
          <span className="font-medium text-foreground/75">First Chapter PDF</span> — a keepsake for the beginning of their story. Coming to Legacy Book export.
        </p>
        {canEdit ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="rounded-full shrink-0"
            onClick={() => onNavigate?.('export')}
          >
            Preview Legacy Book
          </Button>
        ) : null}
      </div>
    </section>
  );
}

export default FirstChapterProfileCard;
